/**
 * TanStack Query configuration and the two hooks the app is allowed to use.
 *
 * Retry policy is the interesting part. The defaults retry everything three
 * times, which is wrong for this API in two directions:
 *
 *   - A 4xx is a verdict, not a blip. Retrying a 422 "your file is not a PDF"
 *     three times just delays the error by two seconds and triples the log noise.
 *     A 401 must fall through to the auth layer immediately, and a 429 retried
 *     eagerly is how you get rate-limited harder.
 *   - Offline is not a retryable condition either: there is no point burning
 *     attempts while `navigator.onLine` is false. TanStack Query already refetches
 *     on reconnect, so the correct move is to fail fast and let the UI say
 *     "you're offline" — then the reconnect refetch handles recovery.
 *
 * So: retry transport blips and 5xx (twice, exponential), nothing else, ever.
 */
import {
  QueryClient,
  useMutation,
  useQuery,
  type QueryKey,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';

import { ApiError, toApiError } from './client';

const MAX_RETRIES = 2;

/** Shared by queries and mutations so a 4xx never gets retried anywhere. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRIES) return false;
  if (error instanceof ApiError) return error.isRetryable;
  // An unrecognised throw is a bug in our code, not a flaky network.
  return false;
}

export function retryDelay(attemptIndex: number): number {
  // 400ms, 800ms — short enough that a user does not think the app hung.
  return Math.min(400 * 2 ** attemptIndex, 4_000);
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        retryDelay,
        // 30s: meta and print options barely change; order state is refetched
        // explicitly on the screens that care.
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        // Mobile users background the tab constantly; refetching on every return
        // to the app is jarring and expensive on metered data.
        refetchOnWindowFocus: false,
        // But a reconnect genuinely means "your data may be stale", so do refetch.
        refetchOnReconnect: true,
        throwOnError: false,
      },
      mutations: {
        // Mutations are not idempotent unless the server says so; only retry
        // transport failures and 5xx, and only where an idempotency key is sent.
        retry: false,
        throwOnError: false,
      },
    },
  });
}

/** The app-wide client. Tests build their own via `createQueryClient()`. */
export const queryClient = createQueryClient();

// --- Conventions -----------------------------------------------------------

/**
 * `useApiQuery` is `useQuery` with the error type pinned to `ApiError`.
 *
 * Pinning the error type is the whole point: every consumer can read
 * `error.code`, `error.isOffline` and `error.requestId` without narrowing, so
 * error UI stays consistent instead of each screen inventing its own guard.
 */
export function useApiQuery<TData, TKey extends QueryKey = QueryKey>(
  options: UseQueryOptions<TData, ApiError, TData, TKey>,
): UseQueryResult<TData, ApiError> {
  return useQuery<TData, ApiError, TData, TKey>(options);
}

/**
 * `useApiMutation` normalises anything thrown into an `ApiError` before it
 * reaches `onError`, so a mutation handler never has to deal with `unknown`.
 */
export function useApiMutation<TData, TVariables, TContext = unknown>(
  options: UseMutationOptions<TData, ApiError, TVariables, TContext> & {
    mutationFn: (variables: TVariables) => Promise<TData>;
  },
): UseMutationResult<TData, ApiError, TVariables, TContext> {
  const { mutationFn, ...rest } = options;
  return useMutation<TData, ApiError, TVariables, TContext>({
    ...rest,
    mutationFn: async (variables: TVariables) => {
      try {
        return await mutationFn(variables);
      } catch (thrown) {
        throw toApiError(thrown);
      }
    },
  });
}

/**
 * Query keys in one place. Strings scattered across components are how two
 * screens end up with two different caches for the same resource.
 */
export const queryKeys = {
  meta: ['meta'] as const,
  health: ['health'] as const,
  upload: (uploadId: string) => ['upload', uploadId] as const,
  quote: (quoteId: string) => ['quote', quoteId] as const,
  /** The cart. No id: the server answers "what is this person's basket?". */
  currentBasket: () => ['quote', 'current'] as const,
  order: (orderId: string) => ['order', orderId] as const,
  orderByNumber: (orderNo: string) => ['order-by-number', orderNo] as const,
  myOrders: (limit: number) => ['my-orders', limit] as const,
  orders: ['orders'] as const,
  // --- checkout flow -------------------------------------------------------
  /** The catalogue of presets. Changes only when an operator edits pricing. */
  printOptions: ['print-options'] as const,
  bindingOptions: ['binding-options'] as const,
  /**
   * Per-preset totals for one upload. Keyed on everything that changes a price,
   * so switching copies or entering a pincode is a cache miss (a new price) and
   * switching back is a cache hit (the same price) — which is the difference
   * between a responsive options screen and one that re-quotes on every render.
   */
  optionPrices: (
    uploadId: string,
    copies: number,
    pincode: string | null,
    bindingSlug: string | null,
    // The rest of the basket and the fulfilment method both move every total on
    // the screen — delivery is quoted once for the whole parcel — so they are
    // part of the key. Without them, adding a second file would leave the first
    // file's cached prices on display.
    basketKey: string,
  ) => ['option-prices', uploadId, copies, pincode ?? '', bindingSlug ?? '', basketKey] as const,
  bindingPrices: (
    uploadId: string,
    printOptionSlug: string,
    copies: number,
    pincode: string | null,
    basketKey: string,
  ) => ['binding-prices', uploadId, printOptionSlug, copies, pincode ?? '', basketKey] as const,
  serviceability: (pincode: string) => ['serviceability', pincode] as const,
  addresses: ['addresses'] as const,
  // --- admin ----------------------------------------------------------------
  adminDashboard: () => ['admin', 'dashboard'] as const,
  adminQueue: (filters: Record<string, unknown>) => ['admin', 'queue', filters] as const,
  adminOrder: (orderId: string) => ['admin', 'order', orderId] as const,
  adminShopStatus: () => ['admin', 'shop'] as const,
  adminPrintOptions: () => ['admin', 'print-options'] as const,
  adminBindingOptions: () => ['admin', 'binding-options'] as const,
  adminShippingRates: () => ['admin', 'shipping-rates'] as const,
  adminShippingZones: () => ['admin', 'shipping-zones'] as const,
  session: ['session'] as const,
} as const;
